import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "../components/SectionHeading";
import CtaBand from "../components/CtaBand";
import TechBackdrop from "../components/TechBackdrop";
import { news } from "../mock";
import { Search, Clock, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const categories = ["All", ...Array.from(new Set(news.map((n) => n.category)))];

export default function NewsPage() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  const filtered = useMemo(() => {
    return news.filter((n) =>
      (cat === "All" || n.category === cat) &&
      (n.title.toLowerCase().includes(q.toLowerCase()) || n.excerpt.toLowerCase().includes(q.toLowerCase()))
    );
  }, [q, cat]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <section className="relative pt-36 lg:pt-44 pb-12 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <TechBackdrop variant="circuit" opacity={0.06} className="hidden lg:block" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <SectionHeading
            eyebrow="News & notes"
            title="Engineering notes from the Power coalface."
            description="Real project debriefs, technical how-tos and opinions — written by the engineers who do the work."
          />
        </div>
      </section>

      <section className="pb-6">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col md:flex-row gap-4 md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles" className="pl-9 h-11 rounded-full bg-white" />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-4 h-9 rounded-full text-[13px] font-medium border transition-colors ${
                  cat === c
                    ? "bg-brand-700 text-white border-brand-700"
                    : "bg-white text-slate-700 border-slate-200 hover:border-brand-400 hover:text-brand-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {featured && (
        <section className="py-6">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Link to={`/news/${featured.id}`} className="group grid lg:grid-cols-12 gap-8 items-center rounded-3xl overflow-hidden border border-slate-200 bg-white hover-lift">
              <div className="lg:col-span-7 relative">
                <img src={featured.image} alt={featured.title} className="w-full h-[360px] lg:h-[440px] object-cover" />
                <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-brand-700 text-[12px] font-medium">
                  {featured.category}
                </div>
              </div>
              <div className="lg:col-span-5 p-7 lg:p-10">
                <div className="text-[12px] text-slate-500 flex items-center gap-3">
                  <span>{featured.date}</span>
                  <span className="h-1 w-1 rounded-full bg-slate-300" />
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{featured.readTime}</span>
                </div>
                <h3 className="font-display mt-3 text-2xl lg:text-3xl font-bold text-slate-900 leading-tight group-hover:text-brand-700 transition-colors">
                  {featured.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-brand-700 font-medium">
                  Read the article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {rest.map((n) => (
            <Link key={n.id} to={`/news/${n.id}`} className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover-lift flex flex-col">
              <div className="relative h-48 overflow-hidden">
                <img src={n.image} alt={n.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-brand-700 text-[11px] font-medium">
                  {n.category}
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="text-[12px] text-slate-500 flex items-center gap-2">
                  <span>{n.date}</span>
                  <span className="h-1 w-1 rounded-full bg-slate-300" />
                  <Clock className="h-3 w-3" /> {n.readTime}
                </div>
                <h4 className="font-display mt-2 text-lg font-semibold text-slate-900 leading-snug group-hover:text-brand-700 transition-colors">{n.title}</h4>
                <p className="mt-2 text-[14px] text-slate-600 leading-relaxed flex-1">{n.excerpt}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-brand-700 font-medium text-[13px]">
                  Read more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="max-w-lg mx-auto text-center py-14">
            <div className="font-display text-xl text-slate-900">No articles match that search.</div>
            <p className="mt-2 text-slate-600">Try another keyword or reset the category filter.</p>
            <Button onClick={() => { setQ(""); setCat("All"); }} variant="outline" className="mt-5 rounded-full">
              Reset filters
            </Button>
          </div>
        )}
      </section>

      <CtaBand />
    </>
  );
}
